import React from 'react';
import { LegalTabNavigation } from '../../../../components/LegalTabNavigation';
import { windowUndefined } from '../../../../../helpers/serverRenderingUtils';

import styles from '../../styles.sass';

function scrollToSection(e, id) {
  e.preventDefault();
  if (!windowUndefined()) {
    const el = document.getElementById(id);
    el.scrollIntoView();
  }
}

export function PrivacyPolicy({ pathname }) {
  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <h1 className={styles.h1}>Anchor Privacy Policy</h1>
        <LegalTabNavigation pathname={pathname} />
        <p>Effective as of March 1, 2022</p>
        <ol>
          <li>
            <a href="#about" onClick={e => scrollToSection(e, 'about')}>
              About this Policy
            </a>
          </li>
          <li>
            <a href="#rights" onClick={e => scrollToSection(e, 'rights')}>
              Your rights and your preferences: Giving you choice and control
            </a>
          </li>
          <li>
            <a href="#collect" onClick={e => scrollToSection(e, 'collect')}>
              Personal data we collect about you
            </a>
          </li>
          <li>
            <a href="#use" onClick={e => scrollToSection(e, 'use')}>
              Our purpose for using your personal data
            </a>
          </li>
          <li>
            <a href="#share" onClick={e => scrollToSection(e, 'share')}>
              Sharing your personal data
            </a>
          </li>
          <li>
            <a href="#retention" onClick={e => scrollToSection(e, 'retention')}>
              Data retention and deletion
            </a>
          </li>
          <li>
            <a href="#transfer" onClick={e => scrollToSection(e, 'transfer')}>
              Transfer to other countries
            </a>
          </li>
          <li>
            <a href="#safe" onClick={e => scrollToSection(e, 'safe')}>
              Keeping your personal data safe
            </a>
          </li>
          <li>
            <a href="#children" onClick={e => scrollToSection(e, 'children')}>
              Children
            </a>
          </li>
          <li>
            <a href="#changes" onClick={e => scrollToSection(e, 'changes')}>
              Changes to this Policy
            </a>
          </li>
          <li>
            <a href="#contact" onClick={e => scrollToSection(e, 'contact')}>
              How to contact us
            </a>
          </li>
        </ol>
        <section id="about">
          <h2>1. About this Policy</h2>
          <p>
            This Policy describes how we process your data when you use Anchor,
            a service provided by Spotify.
          </p>
          <p>
            It applies to your use of Anchor (&quot;Anchor&quot; or &quot;Anchor
            Service&quot;) and any recordings, audio, transcript, or other
            material that is made available through Anchor. References to
            &quot;Spotify&quot;, &quot;we&quot;, &quot;us&quot;, or
            &quot;our&quot; are to the legal entity indicated at{' '}
            <a href="#contact" onClick={e => scrollToSection(e, 'contact')}>
              Section 11 &quot;How to contact us&quot;
            </a>{' '}
            of this Policy below.
          </p>
          <p>
            From time to time, we may develop new or offer additional services.
            They’ll also be subject to this Policy, unless stated otherwise when
            we introduce them.
          </p>
          <p>
            This Policy is <strong>not...</strong>
          </p>
          <ul>
            <li>
              the <a href="/tos">Anchor Terms of Service.</a> That’s a separate
              document, outlining the legal contract between you and Spotify for
              using the Anchor Service. It also describes the rules of the
              Anchor Service and your user rights.
            </li>
            <li>
              about your use of other services provided by Spotify which have
              their own dedicated privacy policy, such as Spotify, Soundtrap,
              Megaphone and Greenroom.
            </li>
          </ul>
          <p>
            However, the{' '}
            <a href="https://www.spotify.com/legal/privacy-policy/">
              Spotify Privacy Policy
            </a>{' '}
            may also apply if you choose to link your Anchor account with your
            Spotify account to unlock certain additional functionalities.
          </p>
        </section>
        <section id="rights">
          <h2>
            2. Your rights and your preferences: Giving you choice and control
          </h2>
          <p>
            Privacy laws, including the General Data Protection Regulation (
            <strong>&quot;GDPR&quot;</strong>
            ), give rights to individuals over their personal data.
          </p>
          <p>See your rights and their descriptions in this table.</p>
          <table className={styles.twoColTable} border="1">
            <thead>
              <tr>
                <th>&nbsp;</th>
                <th>It’s your right to...</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Access</td>
                <td>
                  Be informed of the personal data we process about you and to
                  request access to it
                </td>
              </tr>
              <tr>
                <td>Rectification</td>
                <td>
                  Request that we amend or update your personal data where it’s
                  inaccurate or incomplete
                </td>
              </tr>
              <tr>
                <td>Erasure</td>
                <td>Request that we delete certain of your personal data</td>
              </tr>
              <tr>
                <td>Restriction</td>
                <td>
                  Request that we temporarily or permanently stop processing all
                  or some of your personal data
                </td>
              </tr>
              <tr>
                <td>Object</td>
                <td>
                  <p>
                    Object to us processing your personal data at any time, on
                    grounds relating to your particular situation
                  </p>
                  <p>
                    Object to your personal data being processed for direct
                    marketing purposes
                  </p>
                </td>
              </tr>
              <tr>
                <td>Data portability</td>
                <td>
                  Request a copy of your personal data in electronic format and
                  the right to transmit that personal data for use in another
                  party’s service
                </td>
              </tr>
              <tr>
                <td>Not be subject to automated decision-making</td>
                <td>
                  Not be subject to a decision based solely on automated
                  decision making (decisions without human involvement),
                  including profiling, where the decision would have a legal
                  effect on you or produce a similarly significant effect
                </td>
              </tr>
            </tbody>
          </table>
          <br />
          <p>
            If you would like to exercise any of these rights, you can contact
            us at <a href="mailto:privacy@anchor.fm">privacy@anchor.fm</a>. If
            you would like to change your push notification preferences, you can
            use the notification settings on your mobile device. Email marketing
            messages also include an opt-out mechanism within the message itself
            (e.g. an unsubscribe link in the emails we send you).
          </p>
          <p>
            You also have the right to contact the{' '}
            <a href="https://www.imy.se/">
              Swedish Authority for Privacy Protection
            </a>
            or your local data protection authority about any questions or
            concerns.
          </p>
          <p>
            If you are a resident of the state of California, after January 1,
            2020, please see our supplemental policy{' '}
            <a href="/ccpa">
              &quot;Additional California Privacy Disclosures&quot;
            </a>{' '}
            which will discuss additional privacy rights you may have under
            applicable law regarding the processing of your personal data.
          </p>
        </section>
        <section id="collect">
          <h2>3. Personal data we collect from you</h2>
          <p>
            These tables set out the categories of personal data we collect and
            use.
          </p>
          <table className={styles.twoColTable} border="1">
            <thead>
              <tr>
                <th colSpan={2}>
                  Collected when you sign up for the Anchor Service or when you
                  update your account
                </th>
              </tr>
              <tr>
                <td>Categories</td>
                <td>Description</td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>User Data</td>
                <td>
                  <p>
                    Personal data that we need to create your Anchor account and
                    enables you to use the Anchor Service. The type of data
                    collected depends on if you use third party services to sign
                    in. This may include:
                  </p>
                  <ul>
                    <li>name</li>
                    <li>email address</li>
                    <li>password</li>
                    <li>date of birth</li>
                    <li>country</li>
                  </ul>
                  <p>
                    We receive some of this data from you, e.g. from the sign up
                    form or account page. We also collect some of this data from
                    your device e.g. country. For more information about how we
                    collect country, see &quot;Your general (non-precise)
                    location&quot;.
                  </p>
                  <p>
                    You also have the option to provide us other information
                    about yourself, such as:
                  </p>
                  <ul>
                    <li>website</li>
                    <li>social media handles</li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
          <br />
          <table className={styles.twoColTable} border="1">
            <thead>
              <tr>
                <th colSpan={2}>
                  Collected through your use of the Anchor Service
                </th>
              </tr>
              <tr>
                <td>Categories</td>
                <td>Description</td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Usage Data</td>
                <td>
                  <p>
                    Personal data collected about you when you’re accessing
                    and/or using the Anchor Service. There are a few types of
                    information this includes, detailed in the following
                    sections.
                  </p>
                  <p>
                    <strong>Information about how you use Anchor</strong>
                  </p>
                  <p>Examples include:</p>
                  <ul>
                    <li>
                      your actions with the Anchor Service (including data and
                      time) such as:
                      <ul>
                        <li>features you use</li>
                        <li>actions you take</li>
                        <li>account settings </li>
                        <li>
                          your use of third party services, and applications in
                          connection with the Anchor Service.
                        </li>
                      </ul>
                    </li>
                    <li>
                      content you create, post, upload, and/or share using the
                      Anchor Service.
                    </li>
                  </ul>
                  <p>
                    <strong>Your technical data</strong>
                  </p>
                  <p>Examples include:</p>
                  <ul>
                    <li>URL information</li>
                    <li>
                      online identifiers such as cookie data and IP addresse
                    </li>
                    <li>information about the devices you use such as</li>
                    <ul>
                      <li>device IDs</li>
                      <li>
                        networks connection type (e.g. wifi, 4G, LTE, Bluetooth)
                      </li>
                      <li>provider</li>
                      <li>network and device performance</li>
                      <li>browser type</li>
                      <li>language</li>
                      <li>operating system</li>
                    </ul>
                  </ul>
                  <p>
                    <strong>Your general (non-precise) location</strong>
                  </p>
                  <p>
                    This may be understood from technical data (e.g. your IP
                    address, language setting of your device, or payment
                    currency).
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
          <br />
          <table className={styles.twoColTable} border="1">
            <thead>
              <tr>
                <th colSpan={2}>Additional data you may choose to give us</th>
              </tr>
              <tr>
                <td>Categories</td>
                <td>Description</td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Payment and Purchase Data</td>
                <td>
                  <p>
                    If you are a listener and choose to support a creator, we
                    collect your name and email address. Our payment processor
                    collects your payment information.
                  </p>
                  <p>
                    If you are a creator, monetizing your content on the Anchor
                    Service, we will collect and process personal data for the
                    purposes of providing you payouts and administrating your
                    tax reports.
                  </p>
                  <p>This may include:</p>
                  <ul>
                    <li>legal name</li>
                    <li>business name </li>
                    <li>home address </li>
                    <li>phone number </li>
                    <li>social security number</li>
                    <li>tax identification number</li>
                  </ul>
                  <p>
                    Our payment partner also collects your bank account
                    information.
                  </p>
                </td>
              </tr>
              <tr>
                <td>Contest, Surveys, and Sweepstakes Data</td>
                <td>
                  When you complete any forms, respond to a survey or
                  questionnaire, or take part in a contest or sweepstakes, we
                  collect the personal data you provide.
                </td>
              </tr>
            </tbody>
          </table>
          <br />
          <table className={styles.twoColTable} border="1">
            <thead>
              <tr>
                <th colSpan="2">
                  Collected from other (&apos;third party&apos;) sources
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Categories</td>
                <td>Description</td>
              </tr>
              <tr>
                <td>Authentication partners</td>
                <td>
                  If you register for or log into the Anchor Service, using
                  another service, we’ll receive your information from them to
                  help create your account with us.
                </td>
              </tr>
              <tr>
                <td>
                  Third party applications and services you connect to your
                  Anchor account
                </td>
                <td>
                  <p>
                    If you connect your Anchor account to a third party
                    application and/or service, we may collect certain
                    information from them to make the integration possible.
                  </p>
                  <p>
                    We’ll ask for your permission before we collect your
                    information from certain third parties.
                  </p>
                </td>
              </tr>
              <tr>
                <td>Technical service partners</td>
                <td>
                  We work with technical service partners that give us certain
                  data, such as mapping IP addresses to non-precise location
                  data (e.g. city, state).
                </td>
              </tr>
            </tbody>
          </table>
        </section>
        <section id="use">
          <h2>4. Our purpose for using your personal data</h2>
          <p>The table below sets out:</p>
          <ul>
            <li>our purpose of processing your personal data</li>
            <li>
              our legal justifications (each called a &quot;legal basis&quot;)
              under data protection law, for each purpose
            </li>
            <li>
              categories of personal data which we use for each purpose (see
              more about these categories in Section 3 &apos;Personal data we
              collect about you&apos;).
            </li>
          </ul>
          <p>
            Here is a general explanation of each &quot;legal basis&quot; to
            help you understand the table:
          </p>
          <ul>
            <li>
              <strong>Performance of a Contract:</strong> When it’s necessary
              for Spotify (or a third party) to process your personal data to:
              <ul>
                <li>
                  comply with obligations under a contract with you, e.g.
                  Spotify’s obligations under the Terms of Use to provide the
                  Anchor Service to you
                </li>
                <li>
                  or verify information before a new contract with you begins.
                </li>
              </ul>
            </li>
            <li>
              <strong>Legitimate Interest:</strong> When Spotify (or a third
              party) has an interest in using your personal data in a certain
              way, which is necessary and justified considering any possible
              risks to you and other Anchor users. For example, using your Usage
              Data to improve the Anchor Service for all users. Contact us if
              you want to understand a specific justification.
            </li>
            <li>
              <strong>Consent:</strong> When Spotify asks you to actively
              indicate your agreement to its use of your personal data for a
              certain purpose(s).
            </li>
            <li>
              <strong>Compliance with Legal Obligations:</strong> When Spotify
              must process your personal data to comply with a law.
            </li>
          </ul>
          <br />
          <table className={styles.threeColTable} border="1">
            <thead>
              <tr>
                <th>Purpose for processing your data</th>
                <th>Legal Basis</th>
                <th>Categories of personal data used for the purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>To provide and personalized the Anchor Service.</td>
                <td>
                  <ul>
                    <li>Performance of a Contract</li>
                    <li>Legitimate Interest</li>
                    <li>Consent</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>User Data</li>
                    <li>Usage Data</li>
                    <li>Payment and Purchase Data</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  To understand, diagnose, troubleshoot, and fix issues with the
                  Anchor Service.
                </td>
                <td>
                  <ul>
                    <li>Performance of a Contract</li>
                    <li>Legitimate Interest</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>User Data</li>
                    <li>Usage Data</li>
                    <li>Payment and Purchase Data</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  To evaluate and develop new features, technologies, and
                  improvements to the Anchor Service.
                </td>
                <td>
                  <ul>
                    <li>Legitimate Interest</li>
                    <li>Consent</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>User Data</li>
                    <li>Usage Data</li>
                    <li>Contests, Surveys and Sweepstakes Data</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>For marketing, promotion, and advertising purposes.</td>
                <td>
                  <ul>
                    <li>Legitimate Interest</li>
                    <li>Consent</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>User Data</li>
                    <li>Usage Data</li>
                    <li>Contests, Surveys and Sweepstakes Data</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  To comply with legal obligations and law enforcement requests.
                </td>
                <td>
                  <ul>
                    <li>Compliance with legal obligation</li>
                    <li>Legitimate interest</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>User Data</li>
                    <li>Usage Data</li>
                    <li>Payment and Purchase Data</li>
                    <li>Contests, Surveys and Sweepstakes Data</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  To fulfill contractual obligations with third parties and to
                  enforce our content policies, for example take appropriate
                  action with respect to reports of intellectual property
                  infringement and inappropriate content.
                </td>
                <td>
                  <ul>
                    <li>Legitimate interest</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>User Data</li>
                    <li>Usage Data</li>
                    <li>Payment and Purchase Data</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>To establish, exercise, or defend legal claims.</td>
                <td>
                  <ul>
                    <li>Legitimate interest</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>User Data</li>
                    <li>Usage Data</li>
                    <li>Payment and Purchase Data</li>
                    <li>Contests, Surveys and Sweepstakes Data</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  To conduct business planning, reporting, and forecasting.
                </td>
                <td>
                  <ul>
                    <li>Legitimate Interest</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>User Data</li>
                    <li>Usage Data</li>
                    <li>Payment and Purchase Data</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>To process and administer payments.</td>
                <td>
                  <ul>
                    <li>Performance of a Contract</li>
                    <li>Compliance with legal obligations</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>User Data</li>
                    <li>Payment and Purchase Data</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  To detect and prevent fraud, including fraudulent payments and
                  fraudulent use of the Anchor Service.
                </td>
                <td>
                  <ul>
                    <li>Performance of a Contract</li>
                    <li>Compliance with legal obligations</li>
                    <li>Legitimate Interest</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>User Data</li>
                    <li>Usage Data</li>
                    <li>Payment and Purchase Data</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  To conduct research, contests, surveys, and sweepstakes.
                </td>
                <td>
                  <ul>
                    <li>Performance of a Contract</li>
                    <li>Legitimate Interest</li>
                    <li>Consent</li>
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>User Data</li>
                    <li>Usage Data</li>
                    <li>Contests, Surveys and Sweepstakes Data</li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
        <section id="share">
          <h2>5. Sharing your personal data</h2>
          <p>
            This section sets out the categories of recipients of the personal
            data collected or generated through your use of the Anchor Service.
          </p>
          <p>
            <strong>Publicly available information</strong>
          </p>
          <p>
            The following personal data will always be publicly available on the
            Anchor Service:
          </p>
          <ul>
            <li>
              public posts and/or content that are immediately delivered via RSS
              feeds and/or our APIs to our partners and other third parties,
              including search engines, developers, and publishers that
              integrate the Services content into their services
            </li>
          </ul>
          <p>
            <strong>Personal data you may choose to share</strong>
          </p>
          <p>
            We will only share the following personal data with those outlines
            in the table below:
          </p>
          <ul>
            <li>
              where sharing of particular personal data is needed for the use of
              an Anchor Service feature, or a third party application or
              service, which you have chosen to use; or
            </li>
            <li>
              if you otherwise grant us your permission to share the personal
              data, e.g. by selecting the appropriate setting in the Spotify
              Service or by giving your consent
            </li>
          </ul>
          <br />
          <table className={styles.twoColTable} border="1">
            <thead>
              <tr>
                <th>Categories of recipients</th>
                <th>Reason for sharing</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Third party applications, and services you connect to your
                  Anchor Account
                </td>
                <td>
                  To connect your Anchor account, and/or allow you to use the
                  Anchor Service in connection with third party applications, or
                  services.
                </td>
              </tr>
            </tbody>
          </table>
          <br />
          <p>
            <strong>Information we may share</strong>
          </p>
          <p>See this table for details of who we share to and why.</p>
          <table className={styles.twoColTable} border="1">
            <thead>
              <tr>
                <th>Categories of recipients</th>
                <th>Reason for sharing</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Service Providers</td>
                <td>
                  <p>
                    So they can provide their services to Spotify, to facilitate
                    the Anchor Service.
                  </p>
                  <p>These service providers may include those we hire to:</p>
                  <ul>
                    <li>give customer support</li>
                    <li>
                      operate the technical infrastructure we need to provide
                      the Anchor Service
                    </li>
                    <li>
                      assist in protecting and securing our systems and services
                    </li>
                    <li>
                      help market Spotify’s (and our partners’) products,
                      services, events and promotions
                    </li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>Payment partners</td>
                <td>
                  So they can process your payment and/or our payouts, and for
                  anti-fraud purposes.
                </td>
              </tr>
              <tr>
                <td>Marketing Partners</td>
                <td>
                  <p>
                    To promote Anchor with our partners. We share some personal
                    data with these partners where necessary to:
                  </p>
                  <ul>
                    <li>enable you to participate in Anchor promotions</li>
                    <li>
                      to promote Anchor in media and advertising published on
                      other online services
                    </li>
                    <li>
                      to help us and our partners to measure the effectiveness
                      of Anchor promotions
                    </li>
                  </ul>
                  <p>
                    Our partners may also combine the personal data we share
                    with them with other data they collect about you (e.g. your
                    use of their service). We and our partners may use this
                    information to present you with offers, promotions, or other
                    marketing activities that we believe will be relevant to
                    you.
                  </p>
                </td>
              </tr>
              <tr>
                <td>Other Spotify group companies</td>
                <td>
                  Where we have offices, to carry out our daily business
                  operations and so we can maintain and provide the Anchor
                  Service to you.
                </td>
              </tr>
              <tr>
                <td>Law enforcement and other authorities</td>
                <td>
                  <p>
                    When we believe in good faith it’s necessary for us to do
                    so, for example:
                  </p>
                  <ul>
                    <li>to comply with a legal obligation</li>
                    <li>
                      to respond to a valid legal process (such as a search
                      warrant, court order, or subpoena)
                    </li>
                    <li>
                      for our own or a third party’s justifiable interest,
                      relating to:
                      <ul>
                        <li>national security</li>
                        <li>law enforcement</li>
                        <li>litigation (a court case)</li>
                        <li>criminal investigation</li>
                        <li>protecting someone’s safety</li>
                        <li>preventing death or imminent bodily harm.</li>
                      </ul>
                    </li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>Purchasers of our business</td>
                <td>
                  Where we sell or negotiate to sell our business to a buyer or
                  possible buyer. In this situation, your personal data may be
                  transferred to a successor or affiliate as part of that
                  transaction along with other assets. Spotify will continue to
                  ensure your personal data is kept confidential.
                </td>
              </tr>
            </tbody>
          </table>
        </section>
        <section id="retention">
          <h2>6. Data retention and deletion</h2>
          <p>
            <strong>Retention</strong>
          </p>
          <p>
            We keep your personal data only as long as necessary to provide you
            with the Anchor Service and for legitimate and essential business
            purposes, such as
          </p>
          <ul>
            <li>maintaining the performance of the Anchor Service</li>
            <li>
              making data-driven business decisions about new features and
              offering
            </li>
            <li>complying with our legal obligations</li>
            <li>resolving disputes.</li>
          </ul>
          <p>
            <strong>Deletion</strong>
          </p>
          <p>
            If you close or request that we close your account, we’ll delete or
            anonymise your personal data so it no longer identifies you, unless
            we’re required to keep something or we still need to use it for a
            legally justifiable reason.
          </p>
          <p>
            Here are some examples of situations where we’re legally allowed or
            required to keep some of your personal data:
          </p>
          <ul>
            <li>
              if there’s an unresolved issue relation to your account, such as
              an outstanding credit or unresolved claim or dispute
            </li>
            <li>for our legal, tax, audit and accounting obligations</li>
            <li>
              for our legitimate business interests such as fraud prevention or
              to maintain security
            </li>
          </ul>
        </section>
        <section id="transfer">
          <h2>7. Transfer to other countries</h2>
          <p>
            When carrying out the activities described in this Policy, Spotify
            shares your personal data internationally with other Spotify group
            companies, subcontractors and partners. They may process your
            personal data in countries whose data protection laws are not
            considered to be as strong as EU laws or the laws which apply where
            you live. For example, they may not give you the same rights over
            your data.
          </p>
          <p>
            Whenever we transfer personal data internationally, we use tools to:
          </p>
          <ul>
            <li>make sure the data transfer complies with applicable law</li>
            <li>
              help to give your data the same level of protection as it has in
              the EU
            </li>
          </ul>
          <p>
            We do this using a variety of protections, as appropriate for each
            data transfer. For example, we use:
          </p>
          <ul>
            <li>
              <a href="https://eur-lex.europa.eu/legal-content/EN/TXT/PDF/?uri=CELEX:32021D0914&amp;from=EN">
                Standard Contractual Clauses
              </a>{' '}
              (or an alternative legal tool) to require the third party to
              protect your data and to provide you with EU-level rights and
              protections
            </li>
            <li>
              technical portections, such as encryption and pseudonymisation
            </li>
            <li>
              policies and processes to challenge disproportionate or unlawful
              government authority requests
            </li>
          </ul>
          <p>
            You can exercise your rights under the Standard Contractual Clauses
            by contacting us or the third party who processes your personal
            data.
          </p>
        </section>
        <section id="safe">
          <h2>8. Keeping your personal data safe</h2>
          <p>
            We are committed to protecting our users’ personal data. We
            implement appropriate technical and organisational measures to help
            protect the security of your personal data. However, be aware that
            no system is ever completely secure.
          </p>
          <p>
            We have implemented various safeguards including pseudonymisation,
            encryption, access, and retention policies to guard against
            unauthorised access and unnecessary retention of personal data in
            our systems.
          </p>
          <p>To protect your user account, we encourage you to:</p>
          <ul>
            <li>use a strong password that is unique to your Anchor account</li>
            <li>never share your password with anyone</li>
            <li>limit access to your computer or browser</li>
            <li>
              log out once you have finished using the Anchor Service on a
              shared device
            </li>
          </ul>
        </section>
        <section id="children">
          <h2>9. Children</h2>
          <p>
            The Anchor Service has a minimum “Age Limit” in each country. The
            Anchor Service is not directed to children whose age:
          </p>
          <ul>
            <li>is under the age or 13 years</li>
            <li>or, makes it illegal to process their personal data</li>
            <li>
              or, requires parental consent to process their personal data
            </li>
          </ul>
          <p>
            We do not knowingly collect personal data from children under the
            applicable Age Limit. If you’re under the Age Limit, please do not
            use the Anchor Service, and do not provide any personal data to us.
          </p>
          <p>
            If you’re a parent of a child under the Age Limit and become aware
            that your child has provided personal data to Spotify via the Anchor
            Service, please contact us.
          </p>
          <p>
            If we learn that we’ve collected the personal data of a child under
            the applicable Age Limit, we’ll take reasonable steps to delete the
            personal data. This may require us to delete the Anchor account for
            that child.
          </p>
        </section>
        <section id="changes">
          <h2>10. Changes to this Policy</h2>
          <p>We may occasionally make changes to this Policy.</p>
          <p>
            When we make material changes to this Policy, we’ll provide you with
            prominent notice as appropriate under the circumstances. For
            example, we may display a prominent notice within the Anchor Service
            or send you an email or device notification.
          </p>
        </section>
        <section id="contact">
          <h2>11. How to contact us</h2>
          <p>
            For any questions or concerns about this Policy, please contact our
            Data Protection Officer by using the{' '}
            <a href="https://help.anchor.fm/hc/en-us/requests/new">
              contact form
            </a>
            , emailing <a href="mailto:privacy@anchor.fm">privacy@anchor.fm</a>{' '}
            or by writing to your relevant data controller at the address below.
          </p>
          <p>Data controller if you reside in the US:</p>
          <div className={styles.addressContainer}>
            <span className={styles.addressLine}>Spotify USA, Inc.</span>
            <span className={styles.addressLine}>150 Greenwich St</span>
            <span className={styles.addressLine}>New York, NY 10007</span>
            <span className={styles.addressLine}>USA</span>
          </div>

          <p>Data controller if you reside in any other country than the US:</p>
          <div className={styles.addressContainer}>
            <span className={styles.addressLine}>Spotify AB</span>
            <span className={styles.addressLine}>Regeringsgatan 19</span>
            <span className={styles.addressLine}>111 53 Stockholm</span>
            <span className={styles.addressLine}>Sweden</span>
          </div>
        </section>
      </div>
    </div>
  );
}
